import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, FormControlLabel, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { shippingApi } from './api';

function id() {
  if (!window.crypto?.getRandomValues) throw new Error('Secure request IDs require HTTPS and a supported browser.');
  return Array.from(window.crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
}
export default function BatchQueue({ capabilities, job, revisions, disabled, refreshTick, onBusy, onSelection, onAssigned, onAccessLost }) {
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState('');
  const [detail, setDetail] = useState(null);
  const [station, setStation] = useState('');
  const [container, setContainer] = useState('');
  const [auto, setAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(false);
  const control = useRef(null);
  const pending = useRef(null);
  const inFlight = useRef(false);
  const attempted = useRef(new Set());
  const selectionEpoch = useRef(0);
  const fail = useCallback(err => {
    if (err.name === 'AbortError' || control.current?.signal.aborted) return;
    if (err.status === 401 || err.status === 403) onAccessLost(err);
    else setError(err.message || 'Batch request failed. Refresh before continuing.');
  }, [onAccessLost]);
  const refresh = useCallback(async () => {
    const signal = control.current.signal;
    const epoch = ++selectionEpoch.current;
    const list = await shippingApi.batches(signal);
    const value = selected ? await shippingApi.batch(selected, signal) : null;
    if (signal.aborted || epoch !== selectionEpoch.current) return;
    setBatches(list.batches); setDetail(value);
    onSelection(value?.batch || null);
  }, [selected, onSelection]);
  useEffect(() => {
    const current = new AbortController(); control.current = current;
    return () => { current.abort(); onBusy(false); onSelection(null); };
  }, [onBusy, onSelection]);
  useEffect(() => { refresh().catch(fail); }, [refresh, refreshTick, fail]);
  useEffect(() => { onBusy(busy || retry); }, [busy, retry, onBusy]);
  useEffect(() => {
    if (detail?.batch) { setStation(detail.batch.station_code || ''); setContainer(detail.batch.outgoing_container_code || ''); }
  }, [detail?.batch]);

  const execute = useCallback(async command => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setRetry(false); setError('');
    pending.current = command;
    const signal = control.current.signal;
    try {
      const result = await shippingApi.packingCommand(command.path, command.payload, capabilities.csrf_token, signal);
      if (signal.aborted) return;
      pending.current = null;
      if (result.batch_key && command.path === 'batches') setSelected(result.batch_key);
      if (result.job_id) {
        if (result.status === 'ACTIVE') await onAssigned();
        else { setAuto(false); setError(`Package requires review: ${(result.issues || []).join(', ') || result.status}`); }
      }
      await refresh();
    } catch (err) {
      if (signal.aborted) return;
      setAuto(false);
      if (pending.current && (!err.status || err.status >= 500)) setRetry(true);
      else pending.current = null;
      fail(err);
    } finally { inFlight.current = false; if (!signal.aborted) setBusy(false); }
  }, [capabilities.csrf_token, onAssigned, refresh, fail]);

  // Exactly one automatic attempt per completed binding in this browser session.
  // The server also enforces the predecessor, one ACTIVE job and durable dedupe.
  useEffect(() => {
    if (!auto || !capabilities.batches.auto_next || disabled || busy || retry || !selected || !detail ||
        job?.status !== 'COMPLETE' || job?.physical_state !== 'OUTGOING' || !detail.members.some(m => m.job_id === job.id)) return;
    const token = `${selected}:${job.id}:${job.row_version}:${job.ownership_epoch}`;
    if (attempted.current.has(token)) return;
    attempted.current.add(token);
    execute({ path: `batches/${selected}/claim-next`, payload: { request_key: id(), expected_batch_revision: detail.batch.row_version,
      auto_next: true, previous_job_id: job.id, expected_previous: revisions } });
  }, [auto, capabilities.batches.auto_next, disabled, busy, retry, selected, detail, job, revisions, execute]);

  const blocked = disabled || busy || retry;
  const current = detail?.members.find(row => row.job_id === job?.id);
  function command(path, payload) { try { execute({ path, payload: { ...payload, request_key: id() } }); } catch (err) { fail(err); } }
  return <Paper variant="outlined" sx={{ p: 2 }}><Stack spacing={1.5}>
    <Typography variant="h6">Batch packing</Typography>
    <Typography variant="body2">Queue each package with its own explicit contents and measurements. Two operators use separate accounts. Desk and container codes describe the physical flow; your phone follows your account’s active job.</Typography>
    {error && <Alert severity="warning">{error}</Alert>}
    {retry && <Button disabled={busy} onClick={() => execute(pending.current)}>Retry the same batch command</Button>}
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
      <TextField select label="Batch" value={selected} disabled={blocked} sx={{ minWidth: 230 }} onChange={e => {
        selectionEpoch.current += 1; setSelected(e.target.value); setDetail(null); onSelection(null); setAuto(false);
      }}><MenuItem value="">Choose a batch</MenuItem>{batches.filter(b => b.status === 'OPEN').map(b => <MenuItem key={b.batch_key} value={b.batch_key}>Batch {b.batch_key.slice(0, 8)} · {b.station_code || 'Any desk'} · {b.outgoing_container_code || 'No container code'}</MenuItem>)}</TextField>
      <Button disabled={blocked} onClick={() => refresh().catch(fail)}>Refresh batch</Button>
    </Stack>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
      <TextField label="Desk code (optional)" value={station} disabled={blocked} inputProps={{ maxLength: 32 }} onChange={e => setStation(e.target.value)} />
      <TextField label="Outgoing container code (optional)" value={container} disabled={blocked} inputProps={{ maxLength: 32 }} onChange={e => setContainer(e.target.value)} />
      <Button disabled={blocked} onClick={() => command('batches', { station_code: station || null, outgoing_container_code: container || null })}>Create batch</Button>
      {detail && <Button disabled={blocked} onClick={() => command(`batches/${selected}/routing`, { expected_batch_revision: detail.batch.row_version, station_code: station || null, outgoing_container_code: container || null })}>Update desk / container</Button>}
    </Stack>
    <Typography variant="caption">Use equipment codes only, without buyer information. Hold active work before a physical tray/container swap; resume and recheck afterwards.</Typography>
    {detail && <>
      {current && <Alert severity="info">This package: tray position {current.sequence_no} · Outgoing container {current.outgoing_container_code || 'confirm your physical container'} · Desk {current.station_code || 'any'}.</Alert>}
      <Typography variant="subtitle2">Next available packages · read-only prefetch</Typography>
      {!detail.prefetch.length && <Typography variant="body2">No available packages. Add a package from its order form or review held work.</Typography>}
      {detail.prefetch.map(row => <Typography key={row.job_id} variant="body2">Position {row.sequence_no} · Order {row.order_id} · Package #{row.package_number} · {row.package_key}</Typography>)}
      <Button variant="outlined" disabled={blocked || job?.status === 'ACTIVE' || !detail.prefetch.length} onClick={() => command(`batches/${selected}/claim-next`, {
        expected_batch_revision: detail.batch.row_version, auto_next: false, previous_job_id: null, expected_previous: null,
      })}>Claim next for my account</Button>
      <FormControlLabel control={<Checkbox checked={auto} disabled={blocked || !capabilities.batches.auto_next} onChange={e => { if (e.target.checked) attempted.current.clear(); setAuto(e.target.checked); }} />}
        label="Auto-next for this session after confirmed Outgoing and no unresolved work" />
      {!capabilities.batches.auto_next && <Typography variant="caption">Auto-next remains off until the two-operator trial and barcode/photo configuration are confirmed.</Typography>}
      {detail.recovery_captures?.map(capture => <Stack key={capture.capture_key} direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <Typography variant="body2">Unfinished capture · Job {capture.job_id} · {capture.purpose}. Recover the original photo, or explicitly cancel this capture.</Typography>
        <Button disabled={blocked} onClick={() => command(`captures/${capture.capture_key}/cancel`, {})}>Cancel unfinished capture for job {capture.job_id}</Button>
      </Stack>)}
      <Typography variant="subtitle2">Batch sequence</Typography>
      {detail.members.map(row => <Typography key={row.job_id} variant="body2">{row.sequence_no}. {row.order_id} · Package #{row.package_number} · {row.status} · Container {row.outgoing_container_code || 'manual'}{row.assigned_user_id ? ` · Operator ${row.assigned_user_id}` : ''}</Typography>)}
    </>}
  </Stack></Paper>;
}
